import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuizSocket } from "../../contexts/LiveQuizSocketContext";
import { useTranslation } from "../../hooks/useTranslation";

export default function LiveQuizTab({ classes, moduleId, lang = "de" }) {
  const { socket, setHostActiveRoom } = useLiveQuizSocket();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [quizzes, setQuizzes] = useState([]);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [selectedClass, setSelectedClass] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!moduleId) return;
    fetch(`${process.env.REACT_APP_API_URL || "https://app.fintogo.ai"}/api/livequiz/modules/${moduleId}/quizzes?lang=${lang}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setQuizzes(data);
        } else if (Array.isArray(data?.quizzes)) {
          setQuizzes(data.quizzes);
        } else {
          setError(t.liveQuiz.invalidResponseFormat);
        }
      })
      .catch(() => setError(t.liveQuiz.loadError));
  }, [moduleId, lang]);

  const handleCreate = async () => {
    if (!selectedLessonId || !selectedClass) return;
    setLoading(true);
    setError(null);

    try {
      const sessionRes = await fetch(`${process.env.REACT_APP_API_URL || "https://app.fintogo.ai"}/api/livequiz/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          moduleId,
          lessonId: selectedLessonId,
          classId: selectedClass,
          lang,
          snapshot: true,
        }),
      }).then((r) => r.json());

      if (sessionRes.error) throw new Error(sessionRes.error);

      socket.emit("create_room", { roomCode: sessionRes.roomCode });
      socket.once("room_created", ({ roomCode }) => {
        setLoading(false);
        const selectedQuiz = quizzes.find(q => q.key === selectedLessonId);
        setHostActiveRoom({ roomCode, quizTitle: selectedQuiz?.title || "Live Quiz" });
        navigate(`/livequiz/lobby/${roomCode}`);
      });
      socket.once("create_room_error", ({ message }) => {
        setLoading(false);
        setError(message);
      });
    } catch (e) {
      setLoading(false);
      setError(e.message);
    }
  };

  return (
    <div className="p-3 p-md-4 livequiz-tab">
      <h4 className="fw-bold mb-3">Live Quiz</h4>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="row g-2 g-md-3 mb-4">
        {quizzes.length === 0 && (
          <p className="text-muted">{t.liveQuiz.noQuizzes}</p>
        )}
        {quizzes.map((q) => (
          <div className="col-12 col-sm-6 col-lg-4" key={q.key}>
            <div
              className={`card h-100 border-2 ${
                selectedLessonId === q.key
                  ? "border-primary bg-primary bg-opacity-10"
                  : ""
              }`}
              onClick={() => setSelectedLessonId(q.key)}
              style={{
                cursor: "pointer",
                borderColor: selectedLessonId === q.key ? undefined : "var(--border-color)",
              }}
            >
              <div className="card-body py-2 px-3">
                <h6 className="fw-bold mb-1">{q.title}</h6>
                <small className="text-muted">
                  {t.liveQuiz.questions.replace("{{count}}", q.questionCount)}
                </small>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="d-flex flex-column flex-sm-row gap-2 align-items-stretch align-items-sm-center">
        <select
          className="form-select"
          style={{ maxWidth: "100%" }}
          value={selectedClass}
          onChange={(e) => setSelectedClass(e.target.value)}
        >
          <option value="">{t.liveQuiz.selectClass}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <button
          className="btn btn-primary px-4"
          onClick={handleCreate}
          disabled={!selectedLessonId || !selectedClass || loading}
        >
          {loading ? t.liveQuiz.creatingRoom : t.liveQuiz.createRoom}
        </button>
      </div>
    </div>
  );
}
